import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@core/components/ui/accordion";
import { Link } from "@remix-run/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ClientOnly } from "@core/components/client-only";
import ExtensionPoint from "@core/components/PipelineComponents/ExtensionPoint";
import { InOfficeOrderNavLink } from "@/overrides/med/components/InOfficeOrderNavLink";
import { MarketingCategoryLink } from "@/overrides/site/components/MarketingCategoryLink";
import { useSiteNav } from "@/overrides/site/SiteNavContext";
import { findNavLink, type SiteNavLink } from "@/overrides/site/siteNav";
type FooterLink =
  | { id: string; to: string; label: string }
  | { id: string; marketingCategory: string; label: string }
  | { id: string; requiresInOffice: true; label: string; href: string };

type SectionDef = {
  title: string;
  links: FooterLink[];
};

function renderFooterLink(link: FooterLink) {
  if ("requiresInOffice" in link) {
    return (
      <ClientOnly key={link.id} fallback={null}>
        <InOfficeOrderNavLink label={link.label} href={link.href} />
      </ClientOnly>
    );
  }
  if ("marketingCategory" in link) {
    return (
      <MarketingCategoryLink key={link.id} seoUrl={link.marketingCategory} label={link.label}>
        {link.label}
      </MarketingCategoryLink>
    );
  }
  return (
    <Link key={link.id} to={link.to}>
      {link.label}
    </Link>
  );
}

function orderingLinks(children: SiteNavLink[]): FooterLink[] {
  return children.map((child) =>
    child.behavior === "in-office"
      ? { id: child.id, requiresInOffice: true as const, label: child.label, href: child.href }
      : { id: child.id, to: child.href, label: child.label },
  );
}

export const Footer = () => {
  const { t } = useTranslation();
  const nav = useSiteNav();
  const ordering = nav.find((item) => item.id === "place-order")?.children ?? [];
  const dashboard = findNavLink(nav, "dashboard");
  const about = findNavLink(nav, "about");
  const sections = useMemo((): SectionDef[] => {
    return [
      {
        title: t("ui.ordering", "Ordering"),
        links: orderingLinks(ordering),
      },
      {
        title: t("ui.products", "Products"),
        links: [
          { id: "mkt-metabolic-performance", marketingCategory: "mkt-metabolic-performance", label: t("ui.metabolicPerformance", "Metabolic Performance") },
          { id: "mkt-infusion-therapy", marketingCategory: "mkt-infusion-therapy", label: t("ui.infusionTherapy", "Infusion Therapy") },
          { id: "mkt-bhrt", marketingCategory: "mkt-bhrt", label: t("ui.bhrt", "BHRT") },
          { id: "mkt-regenerative", marketingCategory: "mkt-regenerative", label: t("ui.regenerativeSportsPerformance", "Regenerative & Sports Performance") },
          { id: "mkt-mental-performance", marketingCategory: "mkt-mental-performance", label: t("ui.mentalPerformance", "Mental Performance") },
          { id: "mkt-sexual-performance", marketingCategory: "mkt-sexual-performance", label: t("ui.sexualPerformance", "Sexual Performance") },
        ],
      },
      {
        title: t("ui.myAccount", "My Account"),
        links: [
          ...(dashboard ? [{ id: dashboard.id, to: dashboard.href, label: dashboard.label }] : []),
          { id: "account-settings", to: "/dashboard/settings/profile", label: t("ui.accountSettings", "Account Settings") },
          { id: "patients", to: "/dashboard/patients", label: t("ui.patients", "Patients") },
          { id: "past-orders", to: "/dashboard/orders", label: t("ui.pastOrders", "Past Orders") },
          { id: "addresses", to: "/dashboard/settings/addressbook", label: t("ui.addresses", "Addresses") },
          { id: "payment-methods", to: "/dashboard/wallet", label: t("ui.paymentMethods", "Payment Methods") },
          { id: "invoices", to: "/dashboard/invoices", label: t("ui.invoices", "Invoices") },
        ],
      },
      {
        title: t("ui.company", "Company"),
        links: [
          ...(about ? [{ id: about.id, to: about.href, label: about.label }] : []),
          { id: "our-story", to: "/about#story", label: t("ui.ourStory", "Our Story") },
          { id: "our-mission", to: "/about#mission", label: t("ui.ourMission", "Our Mission") },
          { id: "meet-the-team", to: "/about#team", label: t("ui.meetTheTeam", "Meet the Team") },
        ],
      },
      {
        title: t("ui.support", "Support"),
        links: [
          { id: "support", to: "/support", label: t("ui.supportCenter", "Support Center") },
          { id: "feedback", to: "/feedback", label: t("ui.sendFeedback", "Send Feedback") },
          { id: "legal", to: "/terms", label: t("ui.legalAndPrivacy", "Legal and Privacy") },
        ],
      },
    ];
  }, [t, nav, ordering, dashboard, about]);

  const year = new Date().getFullYear();

  return (
    <section className="site-footer print:hidden" data-testid="site-footer">
      {/* Desktop / tablet sitemap */}
      <div className="footer-texture hidden md:block">
        <div className="sitemap-grid">
          {sections.filter((section) => section.links.length > 0).map((section) => (
            <div key={section.title} className="sitemap-col">
              <h3 className="sitemap-col-title">{section.title}</h3>
              {section.links.map(renderFooterLink)}
            </div>
          ))}
        </div>
        <ExtensionPoint name="_site.footer.message" />
      </div>

      {/* Mobile accordion */}
      <div className="footer-texture md:hidden px-4 py-6">
        <Accordion type="single" collapsible className="space-y-2">
          {sections.filter((section) => section.links.length > 0).map((section) => (
            <AccordionItem key={section.title} value={section.title} className="border-none">
              <AccordionTrigger className="sitemap-col-title flex justify-between items-center">
                {section.title}
              </AccordionTrigger>
              <AccordionContent>
                <div className="sitemap-col">
                  {section.links.map(renderFooterLink)}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <ExtensionPoint name="_site.footer.message" />
      </div>

      <div className="footer-gold-line" />
      <div className="footer-bar">
        Copyright © <span id="footer-year">{year}</span> RD3MG. All rights reserved.
      </div>
    </section>
  );
};
